import velocity from 'velocity-animate';
import { isMobile, isTabletOrDeskTop } from 'common/visualizations/helpers/MediaQueryHelper';

const animationDuration = 300;
const animationEasing = [0.645, 0.045, 0.355, 1];

const ModalFactory = function(element) {
  this.root = element;
  this.dismissals = Array.prototype.slice.apply(element.querySelectorAll('[data-modal-dismiss]'));
  this.openers = Array.prototype.slice.apply(element.querySelectorAll('[data-modal]'));
  this.lastFocusedItem = null;
  this.attachEvents();
};

ModalFactory.prototype = {
  attachEvents: function() {
    this.dismissals.forEach(function(dismissal) {
      dismissal.addEventListener('click', this.dismiss.bind(this));
    }, this);

    this.openers.forEach(function(opener) {
      opener.addEventListener('click', this.open.bind(this));
    }, this);

    document.addEventListener('keyup', function(event) {
      const key = event.which || event.keyCode;

      // ESC
      if (key === 27) {
        const modals = Array.prototype.slice.call(
          document.querySelectorAll('.modal:not(.modal-hidden)')
        );

        modals.forEach(function(modal) {
          // All legacy modals instantiated by this factory will have this
          // attribute; this check allows us to use the same classes in our
          // newer React component without accidentally inheriting behavior,
          // in the event that both legacy and React modals are simultaneously
          // in use. See open() for the line that sets this data attribute.
          if (modal.getAttribute('data-legacy-modal')) {
            modal.classList.add('modal-hidden');
            document.body.classList.remove('modal-open');
            this.restoreFocus(modal);
          }
        }.bind(this));
      }
    }.bind(this));

    window.addEventListener('resize', function() {
      const modals = Array.prototype.slice.call(
        document.querySelectorAll('.modal:not(.modal-hidden)')
      );

      modals.forEach(function(modal) {
        this.reposition(modal.querySelector('.modal-container'));
      }.bind(this));
    }.bind(this));
  },

  open: function(event) {
    let modal = event.target.getAttribute('data-modal');
    modal = this.root.querySelector(`#${modal}`);
    modal.classList.remove('modal-hidden');
    modal.setAttribute('data-legacy-modal', true);

    document.body.classList.add('modal-open');

    const windowWidth = window.innerWidth;
    const modalContainer = modal.querySelector('.modal-container');

    if (isMobile()) {
      modalContainer.style.left = `${windowWidth}px`;

      velocity(modalContainer, {
        left: 0
      }, {
        duration: animationDuration,
        easing: animationEasing
      });
    }

    // Store last focused on element
    this.lastFocusedItem = document.querySelector(':focus');

    // Shift focus to the modal
    const elementToFocus = modalContainer.children[0] || modalContainer;
    elementToFocus.setAttribute('tabindex', '0');
    elementToFocus.focus();
    elementToFocus.style.outline = 'none';

    this.reposition(modalContainer);
  },

  dismiss: function(event) {
    const self = this;
    let target = event.target;

    let closeable = target === event.currentTarget && target.classList.contains('modal-overlay');
    let modal;

    // Find the modal and figure out if it's closeable.
    do {
      if (target.hasAttribute('data-modal-dismiss') &&
          !target.classList.contains('modal')) {
        closeable = true;
      } else if (target.classList.contains('modal')) {
        modal = target;
        break;
      }

      target = target.parentNode;
    } while (target !== self.root);

    if (!modal) {
      return;
    }

    function hideModal() {
      if (closeable) {
        document.body.classList.remove('modal-open');
        modal.classList.add('modal-hidden');

        self.restoreFocus(modal);
      }
    }

    const windowWidth = window.innerWidth;
    const modalContainer = modal.querySelector('.modal-container');

    if (isMobile() && closeable) {
      velocity(modalContainer, {
        left: windowWidth
      }, {
        duration: animationDuration,
        easing: animationEasing,
        complete: hideModal
      });
    } else {
      hideModal();
    }
  },

  restoreFocus: function(modal) {
    const elementToFocus = modal.querySelector('.modal-container').children[0] || modal;
    elementToFocus.removeAttribute('tabindex');
    elementToFocus.style.outline = '';

    // Return focus to the last previously focused on element
    if (this.lastFocusedItem) {
      this.lastFocusedItem.focus();
      this.lastFocusedItem = null;
    }
  },

  reposition: function(modal) {
    if (modal.classList.contains('modal-hidden')) {
      return;
    }

    if (!modal.getAttribute('data-legacy-modal')) {
      return;
    }

    if (isTabletOrDeskTop()) {
      modal.style.margin = '';
    } else {
      modal.style.margin = 0;
    }
  }
};

export default ModalFactory;
